import { FieldShell } from '@today/ui-components'
import { Button, Dialog, Input } from '@today/ui-primitives'
import type { ReactElement } from 'react'

/** Copy of {@link ApiKeyCreateDialogView}; each omitted key falls back to English. */
export interface ApiKeyCreateDialogLabels {
	title: string
	name: string
	lifetime: string
	lifetimeHint: string
	submit: string
	cancel: string
	close: string
}

const DEFAULT_LABELS: ApiKeyCreateDialogLabels = {
	title: 'Create API key',
	name: 'Name',
	lifetime: 'Lifetime (days)',
	lifetimeHint: 'From 1 to 90 days. An unused key also stops working after 30 days.',
	submit: 'Create',
	cancel: 'Cancel',
	close: 'Close',
}

/**
 * Props for {@link ApiKeyCreateDialogView} — controlled: the app owns the draft and its validation.
 *
 * @property days - The lifetime field's raw text (validated by the app).
 * @property error - A server refusal shown inside the still-open dialog.
 */
export interface ApiKeyCreateDialogViewProps {
	isOpen: boolean
	name: string
	days: string
	nameError?: string | null
	daysError?: string | null
	error?: string | null
	isBusy?: boolean
	onNameChange: (name: string) => void
	onDaysChange: (days: string) => void
	onSubmit: () => void
	onCancel: () => void
	labels?: Partial<ApiKeyCreateDialogLabels>
}

/** Asks for a new key's name and lifetime in days (organism, TO-516). */
export function ApiKeyCreateDialogView(props: ApiKeyCreateDialogViewProps): ReactElement {
	const text = { ...DEFAULT_LABELS, ...props.labels }
	return (
		<Dialog
			isOpen={props.isOpen}
			onOpenChange={(open) => (open ? undefined : props.onCancel())}
			title={text.title}
			labels={{ close: text.close }}
		>
			<form
				// The app validates and words the refusals itself.
				noValidate
				className="flex flex-col gap-3"
				onSubmit={(event) => {
					event.preventDefault()
					props.onSubmit()
				}}
			>
				{props.error ? (
					<p role="alert" className="text-ds-sm text-ds-danger">
						{props.error}
					</p>
				) : null}
				<FieldShell label={text.name} error={props.nameError ?? undefined}>
					<Input value={props.name} onChange={(e) => props.onNameChange(e.target.value)} />
				</FieldShell>
				<FieldShell label={text.lifetime} description={text.lifetimeHint} error={props.daysError ?? undefined}>
					<Input
						// Text, not number: the raw text is validated by the app (whole days 1–90).
						type="text"
						inputMode="numeric"
						value={props.days}
						onChange={(e) => props.onDaysChange(e.target.value)}
					/>
				</FieldShell>
				<div className="mt-1 flex gap-2">
					<Button type="submit" isDisabled={props.isBusy}>
						{text.submit}
					</Button>
					<Button variant="muted" onPress={props.onCancel}>
						{text.cancel}
					</Button>
				</div>
			</form>
		</Dialog>
	)
}
